import { useCallback, useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { Button, Checkbox, Select, Table, Tag, TextInput } from '@grovus/design-system'
import { authGeneration } from '../auth/session'
import type { AdminCopy } from '../i18n'
import {
  createAdminCompany, fetchAdminCompanies, fetchAdminCompany, fetchAdminUsers, renameCompany,
  purgeAdminCompany, restoreAdminCompany, type AdminCompany, type AdminCompanyDetail, type AdminUser, type Collision,
  type CreatedCompany, type Deletion,
} from './api'
import { formatDay, formatDayShort } from './format'

interface Props {
  copy: AdminCopy
  narrow: boolean
  /** Every `denied` answer lands here. */
  onLeave: () => void
  /** `true` while a rename is pending, its reconciliation included; the shell locks its navigation. */
  onPending: (pending: boolean) => void
}

type ListView = { status: 'loading' } | { status: 'failed' } | { status: 'ok'; companies: AdminCompany[] }
type StatusFilter = 'all' | AdminCompany['status']
type DetailView =
  | { status: 'loading' }
  | { status: 'failed' }
  | { status: 'gone' }
  | { status: 'ok'; company: AdminCompanyDetail }

const BORDER = '1px solid var(--mantine-color-default-border)'
const FRAME: CSSProperties = { border: BORDER, borderRadius: 'var(--mantine-radius-lg)' }
const TOP: CSSProperties = { verticalAlign: 'top' }
const SKELETON = 'light-dark(var(--mantine-color-ink-1), var(--mantine-color-dark-5))'
const STRIPE = 'light-dark(var(--mantine-color-ink-0), var(--mantine-color-dark-6))'
/** A button that reads as a link: the company name, and the breadcrumb back to the list. */
const LINK: CSSProperties = {
  padding: 0, border: 0, background: 'none', color: 'inherit', font: 'inherit', cursor: 'pointer',
  textDecoration: 'underline', textUnderlineOffset: 3, textDecorationColor: 'var(--mantine-color-default-border)',
}
const DISABLED_LINK: CSSProperties = { ...LINK, cursor: 'default', opacity: 0.5 }
const SKELETON_ROWS = [['190px', '60px', '150px'], ['150px', '70px', '120px'], ['210px', '60px', '170px']]

function Bar({ width }: { width: string }) {
  return <div style={{ height: 12, width, maxWidth: '100%', borderRadius: 'var(--mantine-radius-sm)', background: SKELETON }} />
}

function Muted({ children }: { children: ReactNode }) {
  return <span className="grv-muted">{children}</span>
}

/**
 * The Created attribution (Q5): the path and the creator's email, or the id when no email
 * was kept. An unknown path keeps any creator that is known.
 */
function origin(company: AdminCompany, copy: AdminCopy): string {
  if (company.creator === null) return copy.originUnknown
  const who = company.creator.email ?? company.creator.id
  return company.createdVia === 'unknown' ? `${copy.originUnknown} · ${who}` : copy.origin[company.createdVia](who)
}

function matches(company: AdminCompany, query: string): boolean {
  const q = query.trim().toLowerCase()
  if (q === '') return true
  return company.name.toLowerCase().includes(q) || company.owners.some((o) => o.toLowerCase().includes(q))
}

function Lines({ items, copy }: { items: string[]; copy: AdminCopy }) {
  if (items.length === 0) return <Muted>{copy.none}</Muted>
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
      {items.map((item) => <span key={item} style={{ overflowWrap: 'anywhere' }}>{item}</span>)}
    </div>
  )
}

function Created({ company, copy }: { company: AdminCompany; copy: AdminCopy }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span className="grv-num">{formatDayShort(company.createdAt, copy)}</span>
      <span className="grv-muted" style={{ fontSize: 'var(--mantine-font-size-sm)', overflowWrap: 'anywhere' }}>{origin(company, copy)}</span>
    </div>
  )
}

function NameCell({ company, copy, onOpen }: { company: AdminCompany; copy: AdminCopy; onOpen: () => void }) {
  return (
    <span style={{ display: 'inline-flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}>
      <button type="button" onClick={onOpen} style={{ ...LINK, fontWeight: 600, overflowWrap: 'anywhere', textAlign: 'left' }}>{company.name}</button>
      {company.isDemo && <Tag>{copy.demo}</Tag>}
    </span>
  )
}

/** The purge date has passed and the company still waits for the purge (GRO-138, D7). */
const overdue = (deletion: Deletion, now: number) => new Date(deletion.purgeAfter).getTime() <= now

function requester(deletion: Deletion): string {
  return deletion.requestedBy.email ?? deletion.requestedBy.id
}

/**
 * The status cell of screen 13: "Active", or the "Deletion requested" tag with its
 * "Purge after" line, the full line as the tooltip, and the overdue marker.
 */
function StatusCell({ company, copy }: { company: AdminCompany; copy: AdminCopy }) {
  const { deletion } = company
  if (deletion === null) return <Muted>{copy.active}</Muted>
  const date = formatDay(deletion.purgeAfter, copy)
  return (
    <div title={copy.purgeLine(date, requester(deletion))} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 6, maxWidth: 260, cursor: 'help' }}>
      <Tag color="bad">{copy.deletionRequested}</Tag>
      <span className="grv-muted" style={{ fontSize: 'var(--mantine-font-size-sm)', textDecoration: 'underline dotted', textUnderlineOffset: 3 }}>{copy.purgeOn(date)}</span>
      {overdue(deletion, Date.now()) && <Tag color="warn">{copy.purgeOverdue}</Tag>}
    </div>
  )
}

function Accounts({ count, copy }: { count: number; copy: AdminCopy }) {
  return count === 0 ? <Muted>{copy.none}</Muted> : <span className="grv-num">{count}</span>
}

type UsersView = { status: 'loading' } | { status: 'failed' } | { status: 'ok'; users: AdminUser[] }

interface CreateFormProps {
  copy: AdminCopy
  narrow: boolean
  onLeave: () => void
  onCancel: () => void
  onCreated: (company: CreatedCompany, ownerEmail: string) => void
  /** The outcome of a submit is unknown: the page shows the list filtered to `name`. */
  onUncertain: (name: string) => void
}

/**
 * Screen 15 (GRO-137): the inline create form. The Owner picker lists the users who are not
 * disabled, from the user list. A refusal keeps the form and its values; an unknown outcome
 * keeps them too, with a message that the company may already exist.
 */
function CreateForm({ copy, narrow, onLeave, onCancel, onCreated, onUncertain }: CreateFormProps) {
  const [name, setName] = useState('')
  const [ownerId, setOwnerId] = useState<string | null>(null)
  const [users, setUsers] = useState<UsersView>({ status: 'loading' })
  const [busy, setBusy] = useState(false)
  const [nameError, setNameError] = useState<string | null>(null)
  const [formError, setFormError] = useState<string | null>(null)
  /** False once the form unmounts, so a late answer cannot act on a newer form. */
  const mounted = useRef(true)

  useEffect(() => {
    mounted.current = true
    return () => { mounted.current = false }
  }, [])

  const loadUsers = useCallback(async () => {
    setUsers({ status: 'loading' })
    const result = await fetchAdminUsers()
    if (!mounted.current) return
    if (result.status === 'ok') setUsers({ status: 'ok', users: result.body.filter((u) => !u.disabled) })
    else if (result.status === 'denied') onLeave()
    else if (result.status !== 'stale' && result.status !== 'expired') setUsers({ status: 'failed' })
  }, [onLeave])

  useEffect(() => { void loadUsers() }, [loadUsers])

  const eligible = users.status === 'ok' ? users.users : []
  const byId = new Map(eligible.map((u) => [u.id, u]))
  const noOwner = users.status === 'ok' && eligible.length === 0
  const trimmed = name.trim()
  const canSubmit = !busy && trimmed !== '' && ownerId !== null && byId.has(ownerId)

  async function submit() {
    if (!canSubmit || ownerId === null) return
    const owner = byId.get(ownerId)
    if (owner === undefined) return
    setBusy(true)
    setNameError(null)
    setFormError(null)
    const result = await createAdminCompany(trimmed, ownerId)
    if (!mounted.current) return
    setBusy(false)
    if (result.status === 'ok') return onCreated(result.body, owner.email)
    if (result.status === 'denied') return onLeave()
    if (result.status === 'stale' || result.status === 'expired') return
    if (result.status === 'refused' && result.code === 'duplicate_name') return setNameError(copy.duplicateFor(owner.email, trimmed))
    if (result.status === 'refused' && result.code === 'reserved_name') return setNameError(copy.reservedName)
    if (result.status === 'refused' && result.code === 'empty_name') return
    if (result.status === 'refused' && result.code === 'no_eligible_owner') {
      setOwnerId(null)
      setFormError(copy.ownerIneligible)
      void loadUsers()
      return
    }
    setFormError(copy.createUncertain)
    onUncertain(trimmed)
  }

  const companiesOf = (user: AdminUser) => user.companies.map((c) => `${c.name} · ${copy.roles[c.role]}`).join(', ')

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <form
        aria-label={copy.createCompany}
        onSubmit={(e) => { e.preventDefault(); void submit() }}
        style={{ ...FRAME, display: 'flex', flexDirection: 'column', gap: 16, padding: 20 }}
      >
        <span className="grv-label">{copy.createCompany}</span>
        <div style={{ display: 'flex', flexDirection: narrow ? 'column' : 'row', flexWrap: 'wrap', gap: 16, alignItems: narrow ? 'stretch' : 'flex-start' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0, flex: narrow ? undefined : '1 1 220px', maxWidth: narrow ? undefined : 320 }}>
            <span className="grv-label">{copy.companyName}</span>
            <TextInput
              aria-label={copy.companyName}
              placeholder={copy.namePlaceholder}
              value={name}
              error={nameError ?? undefined}
              onChange={(e) => { setName(e.currentTarget.value); setNameError(null) }}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0, flex: narrow ? undefined : '2 1 280px', maxWidth: narrow ? undefined : 440 }}>
            <span className="grv-label">{copy.owner}</span>
            {users.status === 'failed' ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px 12px' }}>
                <span className="grv-muted" role="alert">{copy.usersFailed}</span>
                <Button variant="default" size="compact-sm" onClick={() => void loadUsers()}>{copy.retry}</Button>
              </div>
            ) : (
              <Select
                aria-label={copy.owner}
                placeholder={copy.ownerPlaceholder}
                searchable
                disabled={noOwner || users.status === 'loading'}
                data={eligible.map((u) => ({ value: u.id, label: u.email }))}
                value={ownerId}
                onChange={(value) => { setOwnerId(value); setNameError(null); setFormError(null) }}
                renderOption={({ option }) => {
                  const user = byId.get(option.value)
                  const sub = user ? companiesOf(user) : ''
                  return (
                    <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', columnGap: 8 }}>
                      <span style={{ fontWeight: 500 }}>{option.label}</span>
                      {sub !== '' && <span className="grv-muted" style={{ fontSize: 'var(--mantine-font-size-sm)' }}>{`· ${sub}`}</span>}
                    </span>
                  )
                }}
              />
            )}
            {noOwner && (
              <div style={{ padding: '10px 12px', border: BORDER, borderRadius: 'var(--mantine-radius-md)' }}>
                <Muted>{copy.noEligibleOwner}</Muted>
              </div>
            )}
          </div>
          <span className="grv-muted" style={{ fontSize: 'var(--mantine-font-size-sm)', flex: narrow ? undefined : '1 1 200px', minWidth: 0, alignSelf: narrow ? undefined : 'flex-end' }}>{copy.ownerPrereq}</span>
        </div>
        {formError !== null && <span role="alert" style={{ color: 'var(--mantine-color-error)' }}>{formError}</span>}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <Button type="submit" size="sm" disabled={!canSubmit} loading={busy}>{copy.createSubmit}</Button>
          <Button variant="default" size="sm" disabled={busy} onClick={onCancel}>{copy.cancel}</Button>
        </div>
      </form>
      <Muted>{copy.createNote}</Muted>
    </div>
  )
}

/**
 * Screens 13 and 14 (GRO-135): every company with its creator, Owners and provenance, and
 * one company read-only; screen 15's create form above the list (GRO-137). The selected
 * company is component state; leaving the section unmounts the page, so coming back starts
 * from a fresh list.
 */
export function CompaniesPage({ copy, narrow, onLeave, onPending }: Props) {
  const [list, setList] = useState<ListView>({ status: 'loading' })
  const [query, setQuery] = useState('')
  const [showDemo, setShowDemo] = useState(false)
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [selected, setSelected] = useState<string | null>(null)
  /** A restore changed a company since the list was read; going back refetches it. */
  const changed = useRef(false)
  const [detail, setDetail] = useState<DetailView>({ status: 'loading' })
  const [creating, setCreating] = useState(false)
  const [banner, setBanner] = useState<{ company: CreatedCompany; ownerEmail: string } | null>(null)
  /** The latest detail request; an answer to any earlier one is dropped. */
  const latest = useRef(0)
  /** The latest list request, likewise: a refresh after a create outranks the first load. */
  const latestList = useRef(0)

  const loadList = useCallback(async () => {
    const request = ++latestList.current
    setList({ status: 'loading' })
    const result = await fetchAdminCompanies()
    if (latestList.current !== request) return
    if (result.status === 'ok') setList({ status: 'ok', companies: result.body })
    else if (result.status === 'denied') onLeave()
    else if (result.status !== 'stale' && result.status !== 'expired') setList({ status: 'failed' })
  }, [onLeave])

  const loadDetail = useCallback(async (id: string) => {
    const request = ++latest.current
    setDetail({ status: 'loading' })
    const result = await fetchAdminCompany(id)
    if (latest.current !== request) return
    if (result.status === 'ok') setDetail({ status: 'ok', company: result.body })
    else if (result.status === 'notFound') setDetail({ status: 'gone' })
    else if (result.status === 'denied') onLeave()
    else if (result.status !== 'stale' && result.status !== 'expired') setDetail({ status: 'failed' })
  }, [onLeave])

  useEffect(() => { void loadList() }, [loadList])
  useEffect(() => () => { latest.current++ }, [])

  function open(id: string) {
    setSelected(id)
    void loadDetail(id)
  }

  function back(refresh: boolean) {
    latest.current++
    setSelected(null)
    if (refresh || changed.current) {
      changed.current = false
      void loadList()
    }
  }

  function restored(company: AdminCompanyDetail) {
    latest.current++
    changed.current = true
    setDetail({ status: 'ok', company })
  }

  /**
   * After a submit the search shows the submitted name under every status, so the row is
   * visible whatever was searched or filtered before.
   */
  function showSubmitted(name: string) {
    setQuery(name)
    setStatusFilter('all')
    void loadList()
  }

  function created(company: CreatedCompany, ownerEmail: string) {
    setCreating(false)
    setBanner({ company, ownerEmail })
    showSubmitted(company.name)
  }

  if (selected !== null) {
    return (
      <Detail
        copy={copy}
        narrow={narrow}
        view={detail}
        onBack={back}
        onRetry={() => void loadDetail(selected)}
        onRestored={restored}
        onRestoredElsewhere={() => {
          changed.current = true
          void loadDetail(selected)
        }}
        onLeave={onLeave}
        onPending={onPending}
        onReplace={(company) => setDetail({ status: 'ok', company })}
        onGone={() => setDetail({ status: 'gone' })}
      />
    )
  }

  const total = list.status === 'ok' ? list.companies.length : 0
  const shown = list.status === 'ok'
    ? list.companies.filter((c) => (showDemo || !c.isDemo) && (statusFilter === 'all' || c.status === statusFilter) && matches(c, query))
    : []
  const filtered = query.trim() !== '' || !showDemo || statusFilter !== 'all'
  const hiddenDemo = list.status === 'ok' && !showDemo && list.companies.some((c) => c.isDemo)

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <h2 style={{ margin: 0, fontSize: 'var(--mantine-font-size-xl)', fontWeight: 700, letterSpacing: '-0.01em' }}>{copy.companiesTitle}</h2>
          {list.status === 'ok' && (
            <span className="grv-muted grv-num" style={{ fontSize: 'var(--mantine-font-size-xl)', fontWeight: 500 }}>
              {filtered && shown.length !== total ? copy.countOf(shown.length, total) : total}
            </span>
          )}
        </div>
        <span className="grv-muted">{copy.companiesSub}</span>
      </div>

      {creating && (
        <CreateForm
          copy={copy}
          narrow={narrow}
          onLeave={onLeave}
          onCancel={() => setCreating(false)}
          onCreated={created}
          onUncertain={showSubmitted}
        />
      )}

      {banner !== null && (
        <div role="status" style={{ ...FRAME, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px 16px', padding: '12px 16px' }}>
          <span style={{ overflowWrap: 'anywhere' }}>{copy.createdBanner(banner.company.name, banner.ownerEmail)}</span>
          <Button variant="default" size="compact-sm" onClick={() => open(banner.company.id)}>{copy.open}</Button>
        </div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px 20px' }}>
        <TextInput
          aria-label={copy.searchCompanies}
          placeholder={copy.searchCompanies}
          value={query}
          onChange={(e) => setQuery(e.currentTarget.value)}
          style={{ flex: '1 1 260px', maxWidth: narrow ? undefined : 360 }}
        />
        <Select
          aria-label={copy.status}
          data={[
            { value: 'all', label: copy.allStatuses },
            { value: 'active', label: copy.active },
            { value: 'deletion_requested', label: copy.deletionRequested },
          ]}
          value={statusFilter}
          onChange={(value) => setStatusFilter((value as StatusFilter | null) ?? 'all')}
          allowDeselect={false}
          style={{ width: 220 }}
        />
        <Checkbox label={copy.showDemo} checked={showDemo} onChange={(e) => setShowDemo(e.currentTarget.checked)} />
        {!creating && (
          <Button style={{ marginLeft: narrow ? 0 : 'auto' }} onClick={() => { setBanner(null); setCreating(true) }}>{copy.createCompany}</Button>
        )}
      </div>

      {list.status === 'failed' && (
        <div style={{ ...FRAME, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px 16px', padding: 24 }}>
          <span className="grv-muted" role="alert">{copy.companiesFailed}</span>
          <Button variant="default" onClick={() => void loadList()}>{copy.retry}</Button>
        </div>
      )}

      {list.status === 'ok' && shown.length === 0 && (
        <div style={{ ...FRAME, display: 'flex', flexDirection: 'column', gap: 4, padding: 24 }}>
          <Muted>{copy.noMatch}</Muted>
          {hiddenDemo && <Muted>{copy.demoHidden}</Muted>}
        </div>
      )}

      {list.status !== 'failed' && !(list.status === 'ok' && shown.length === 0) && !narrow && (
        <div style={FRAME}>
          <Table striped stripedColor={STRIPE} aria-busy={list.status === 'loading'}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th><span className="grv-label">{copy.companyName}</span></Table.Th>
                <Table.Th><span className="grv-label">{copy.status}</span></Table.Th>
                <Table.Th><span className="grv-label">{copy.created}</span></Table.Th>
                <Table.Th><span className="grv-label">{copy.owners}</span></Table.Th>
                <Table.Th><span className="grv-label">{copy.members}</span></Table.Th>
                <Table.Th><span className="grv-label">{copy.accounts}</span></Table.Th>
                <Table.Th><span className="grv-label">{copy.portfolios}</span></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {list.status === 'ok' && shown.map((company) => (
                <Table.Tr key={company.id} data-testid="company-row">
                  <Table.Td style={TOP}><NameCell company={company} copy={copy} onOpen={() => open(company.id)} /></Table.Td>
                  <Table.Td style={TOP}><StatusCell company={company} copy={copy} /></Table.Td>
                  <Table.Td style={TOP}><Created company={company} copy={copy} /></Table.Td>
                  <Table.Td style={TOP}><Lines items={company.owners} copy={copy} /></Table.Td>
                  <Table.Td style={TOP}><span className="grv-num">{company.memberCount}</span></Table.Td>
                  <Table.Td style={TOP}><Accounts count={company.accountCount} copy={copy} /></Table.Td>
                  <Table.Td style={TOP}><Lines items={company.portfolios} copy={copy} /></Table.Td>
                </Table.Tr>
              ))}
              {list.status === 'loading' && SKELETON_ROWS.map(([a, b, c]) => (
                <Table.Tr key={a + b + c}>
                  <Table.Td style={TOP}><Bar width={a} /></Table.Td>
                  <Table.Td style={TOP}><Bar width={b} /></Table.Td>
                  <Table.Td style={TOP}><Bar width="90px" /></Table.Td>
                  <Table.Td style={TOP}><Bar width={c} /></Table.Td>
                  <Table.Td style={TOP}><Bar width="24px" /></Table.Td>
                  <Table.Td style={TOP}><Bar width="24px" /></Table.Td>
                  <Table.Td style={TOP}><Bar width={c} /></Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </div>
      )}

      {list.status !== 'failed' && !(list.status === 'ok' && shown.length === 0) && narrow && (
        <div style={{ ...FRAME, display: 'flex', flexDirection: 'column', overflow: 'hidden' }} aria-busy={list.status === 'loading'}>
          {list.status === 'ok' && shown.map((company, i) => (
            <div key={company.id} data-testid="company-row" style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '12px 16px', borderTop: i === 0 ? 'none' : BORDER, background: i % 2 === 1 ? STRIPE : undefined }}>
              <NameCell company={company} copy={copy} onOpen={() => open(company.id)} />
              <StatusCell company={company} copy={copy} />
              <div style={{ display: 'grid', gridTemplateColumns: '92px minmax(0,1fr)', gap: '6px 12px', alignItems: 'baseline' }}>
                <span className="grv-label">{copy.created}</span>
                <Created company={company} copy={copy} />
                <span className="grv-label">{copy.owners}</span>
                <Lines items={company.owners} copy={copy} />
                <span className="grv-label">{copy.members}</span>
                <span className="grv-num">{company.memberCount}</span>
                <span className="grv-label">{copy.accounts}</span>
                <Accounts count={company.accountCount} copy={copy} />
                <span className="grv-label">{copy.portfolios}</span>
                <Lines items={company.portfolios} copy={copy} />
              </div>
            </div>
          ))}
          {list.status === 'loading' && SKELETON_ROWS.map(([a, b, c], i) => (
            <div key={a + b + c} style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '12px 16px', borderTop: i === 0 ? 'none' : BORDER, background: i % 2 === 1 ? STRIPE : undefined }}>
              <Bar width={a} /><Bar width={b} /><Bar width={c} />
            </div>
          ))}
        </div>
      )}
    </>
  )
}

interface DetailProps {
  copy: AdminCopy
  narrow: boolean
  view: DetailView
  /** `true` refetches the list on the way back. */
  onBack: (refresh: boolean) => void
  onRetry: () => void
  onRestored: (company: AdminCompanyDetail) => void
  /** Another admin restored the company first: the detail is read again, and the list on the way back. */
  onRestoredElsewhere: () => void
  onLeave: () => void
  onPending: (pending: boolean) => void
  /** Shows `company` in place of the loaded detail: a rename's answer or its reconciling read. */
  onReplace: (company: AdminCompanyDetail) => void
  onGone: () => void
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <h3 style={{ margin: 0, fontSize: 'var(--mantine-font-size-md)', fontWeight: 600 }}>{title}</h3>
      {children}
    </section>
  )
}

/** The rename form's state: closed, or open with its value and error. */
type Form = { open: false } | { open: true; value: string; error: string | null }


type RestoreStep =
  | { step: 'idle' }
  | { step: 'confirm' }
  | { step: 'taken'; collisions: Collision[]; name: string; emptyName: boolean }
  | { step: 'sending'; from: 'confirm' | 'taken'; collisions: Collision[]; name: string }
  | { step: 'failed'; from: 'confirm' | 'taken'; collisions: Collision[]; name: string }

/** The purge confirm's state (GRO-139, D7), with the typed name. */
type PurgeStep =
  | { step: 'idle' }
  | { step: 'confirm' | 'sending' | 'failed'; typed: string }

const SUB: CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 640, paddingTop: 12, borderTop: BORDER,
}
const ERROR_TEXT: CSSProperties = { fontSize: 'var(--mantine-font-size-xs)', color: 'var(--mantine-color-error)', overflowWrap: 'anywhere' }

/**
 * Screen 14, frozen (GRO-138, D7): the request, the purge date and Restore. A
 * `duplicate_name` answer turns the confirm into "Restore as", prefilled with the current
 * name; `not_frozen` means another admin restored it first, so the detail is read again.
 * Purge now (GRO-139, D7) asks for the company's name typed out; a purge, or another
 * admin's, goes back to the list.
 */
function FrozenPanel({ company, deletion, copy, onRestored, onReload, onGone, onLeave, onPurging }: {
  company: AdminCompanyDetail
  deletion: Deletion
  copy: AdminCopy
  onRestored: (company: AdminCompanyDetail) => void
  onReload: () => void
  onGone: () => void
  onLeave: () => void
  /** A purge is in flight: the detail keeps the admin on it until the answer lands. */
  onPurging: (purging: boolean) => void
}) {
  const [state, setState] = useState<RestoreStep>({ step: 'idle' })
  const [purge, setPurge] = useState<PurgeStep>({ step: 'idle' })
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => { mounted.current = false }
  }, [])

  async function send(from: 'confirm' | 'taken', collisions: Collision[], name: string) {
    setState({ step: 'sending', from, collisions, name })
    const result = await restoreAdminCompany(company.id, from === 'taken' ? name.trim() : undefined)
    if (!mounted.current) return
    if (result.status === 'ok') onRestored(result.body)
    else if (result.status === 'denied') onLeave()
    else if (result.status === 'notFound') onGone()
    else if (result.status === 'refused' && result.code === 'not_frozen') onReload()
    else if (result.status === 'refused' && result.code === 'duplicate_name') {
      setState({ step: 'taken', collisions: result.collisions, name: from === 'taken' ? name : company.name, emptyName: false })
    } else if (result.status === 'refused' && result.code === 'empty_name') {
      setState({ step: 'taken', collisions, name, emptyName: true })
    } else if (result.status !== 'stale' && result.status !== 'expired') {
      setState({ step: 'failed', from, collisions, name })
    }
  }

  async function sendPurge(typed: string) {
    setPurge({ step: 'sending', typed })
    onPurging(true)
    const result = await purgeAdminCompany(company.id, typed.trim())
    onPurging(false)
    if (!mounted.current) return
    if (result.status === 'ok' || result.status === 'notFound') onGone()
    else if (result.status === 'denied') onLeave()
    else if (result.status === 'refused' && (result.code === 'not_frozen' || result.code === 'name_mismatch')) onReload()
    else if (result.status !== 'stale' && result.status !== 'expired') setPurge({ step: 'failed', typed })
  }

  const sending = state.step === 'sending'
  const failed = state.step === 'failed'
  const from = state.step === 'sending' || state.step === 'failed' ? state.from : state.step
  const collisions = state.step === 'idle' || state.step === 'confirm' ? [] : state.collisions
  const name = state.step === 'idle' || state.step === 'confirm' ? company.name : state.name
  const cancel = () => setState({ step: 'idle' })

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }} data-testid="frozen">
      <span className="grv-label">{copy.deletionRequested}</span>
      <span style={{ maxWidth: 720, overflowWrap: 'anywhere' }}>
        {copy.delText(formatDay(deletion.requestedAt, copy), requester(deletion), formatDay(deletion.purgeAfter, copy))}
      </span>
      {overdue(deletion, Date.now()) && <span><Tag color="warn">{copy.purgeOverdue}</Tag></span>}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        <Button onClick={() => setState({ step: 'confirm' })} disabled={state.step !== 'idle' || purge.step !== 'idle'}>{copy.restore}</Button>
        <Button color="red" onClick={() => setPurge({ step: 'confirm', typed: '' })} disabled={state.step !== 'idle' || purge.step !== 'idle'}>{copy.purgeNow}</Button>
      </div>

      {purge.step !== 'idle' && (
        <div style={SUB}>
          <span style={{ fontSize: 'var(--mantine-font-size-xs)', overflowWrap: 'anywhere' }}>{copy.purgeText(company.name)}</span>
          <TextInput
            label={copy.purgeTyped}
            value={purge.typed}
            disabled={purge.step === 'sending'}
            onChange={(e) => setPurge({ step: 'confirm', typed: e.currentTarget.value })}
          />
          {purge.step === 'failed' && <span role="alert" style={ERROR_TEXT}>{copy.purgeFailed}</span>}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <Button
              size="compact-sm"
              color="red"
              loading={purge.step === 'sending'}
              disabled={purge.typed.trim() !== company.name}
              onClick={() => void sendPurge(purge.typed)}
            >
              {copy.purgeNow}
            </Button>
            <Button size="compact-sm" variant="default" disabled={purge.step === 'sending'} onClick={() => setPurge({ step: 'idle' })}>{copy.cancel}</Button>
          </div>
        </div>
      )}

      {from === 'confirm' && (
        <div style={SUB}>
          <span style={{ fontSize: 'var(--mantine-font-size-xs)' }}>{copy.restoreText(company.name)}</span>
          {failed && <span role="alert" style={ERROR_TEXT}>{copy.restoreFailed}</span>}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <Button size="compact-sm" loading={sending} onClick={() => void send('confirm', [], company.name)}>{copy.restore}</Button>
            <Button size="compact-sm" variant="default" disabled={sending} onClick={cancel}>{copy.cancel}</Button>
          </div>
        </div>
      )}

      {from === 'taken' && (
        <div style={SUB}>
          <span role="alert" style={ERROR_TEXT}>
            {[...collisions.map((c) => copy.takenErr(c.email, c.companyName)), copy.takenRetry].join(' ')}
          </span>
          <TextInput
            label={copy.restoreAs}
            value={name}
            error={state.step === 'taken' && state.emptyName ? copy.emptyName : undefined}
            disabled={sending}
            onChange={(e) => setState({ step: 'taken', collisions, name: e.currentTarget.value, emptyName: false })}
          />
          <span className="grv-muted" style={{ fontSize: 'var(--mantine-font-size-sm)' }}>{copy.takenNote}</span>
          {failed && <span role="alert" style={ERROR_TEXT}>{copy.restoreFailed}</span>}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <Button size="compact-sm" loading={sending} disabled={name.trim() === ''} onClick={() => void send('taken', collisions, name)}>{copy.restore}</Button>
            <Button size="compact-sm" variant="default" disabled={sending} onClick={cancel}>{copy.cancel}</Button>
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * Screen 14, active, demo and frozen states, with its loading, failed and missing states,
 * and the rename of an active company (GRO-136, D5). While a save is pending, nothing
 * leaves the detail: Cancel, Escape and the breadcrumb are disabled, and the shell locks
 * its sections. A pending purge holds the breadcrumb and the shell the same way (GRO-139). A frozen company shows Rename disabled until it is restored (GRO-138, D7).
 */
function Detail({
  copy, narrow, view, onBack, onRetry, onRestored, onRestoredElsewhere, onLeave, onPending, onReplace, onGone,
}: DetailProps) {
  const company = view.status === 'ok' ? view.company : null
  const [form, setForm] = useState<Form>({ open: false })
  const [saving, setSaving] = useState(false)
  const [purging, setPurging] = useState(false)
  const busy = saving || purging
  /** A rename was sent that may have committed: Back refetches the list. */
  const [renamed, setRenamed] = useState(false)
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => { mounted.current = false }
  }, [])

  function fail(error: string) {
    setForm((f) => (f.open ? { ...f, error } : f))
  }

  async function save(id: string, value: string) {
    const name = value.trim()
    if (name === '' || saving) return
    const generation = authGeneration()
    /** The view is still on screen in the session that sent the rename. */
    const current = () => mounted.current && generation === authGeneration()
    setSaving(true)
    onPending(true)
    setForm({ open: true, value, error: null })
    try {
      const result = await renameCompany(id, name)
      if (!current()) return
      if (result.status === 'ok') {
        onReplace(result.body)
        setRenamed(true)
        setForm({ open: false })
        return
      }
      if (result.status === 'denied') return onLeave()
      if (result.status === 'notFound') return onGone()
      if (result.status === 'stale' || result.status === 'expired') return
      if (result.status === 'refused' && result.code === 'duplicate_name') {
        return fail(result.member === null ? copy.nameTaken(name) : copy.renameTaken(result.member, name))
      }

      // The rename may have committed: read the company again before saying anything.
      setRenamed(true)
      const fresh = await fetchAdminCompany(id)
      if (!current()) return
      if (fresh.status === 'ok') {
        onReplace(fresh.body)
        if (fresh.body.name === name) setForm({ open: false })
        else fail(copy.renameFailed)
        return
      }
      if (fresh.status === 'denied') return onLeave()
      if (fresh.status === 'notFound') return onGone()
      if (fresh.status === 'stale' || fresh.status === 'expired') return
      fail(copy.renameFailed)
    } finally {
      if (mounted.current) setSaving(false)
      onPending(false)
    }
  }

  function cancel() {
    if (!saving) setForm({ open: false })
  }

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span className="grv-muted" style={{ fontSize: 'var(--mantine-font-size-sm)' }}>
          <button type="button" disabled={busy} onClick={() => onBack(renamed)} style={busy ? DISABLED_LINK : LINK}>{copy.companiesTitle}</button>
          {company && ` / ${company.name}`}
        </span>
        {company && (
          <>
            {form.open ? (
              <form
                onSubmit={(e) => { e.preventDefault(); void save(company.id, form.value) }}
                style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: '8px 10px' }}
              >
                <TextInput
                  label={copy.nameLabel}
                  value={form.value}
                  error={form.error}
                  autoFocus
                  readOnly={saving}
                  onChange={(e) => { const value = e.currentTarget.value; setForm({ open: true, value, error: null }) }}
                  onKeyDown={(e) => { if (e.key === 'Escape') cancel() }}
                  style={{ flex: narrow ? '1 1 100%' : '0 0 360px', width: narrow ? '100%' : 360 }}
                />
                <div style={{ display: 'flex', gap: 8, paddingTop: 'calc(var(--mantine-font-size-sm) * 1.55 + 2px)' }}>
                  <Button type="submit" disabled={saving || form.value.trim() === ''}>{copy.save}</Button>
                  <Button variant="default" disabled={saving} onClick={cancel}>{copy.cancel}</Button>
                </div>
              </form>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px 10px' }}>
                <h2 style={{ margin: 0, fontSize: 'var(--mantine-font-size-xl)', fontWeight: 700, letterSpacing: '-0.01em', overflowWrap: 'anywhere' }}>{company.name}</h2>
                {company.isDemo && <Tag>{copy.demo}</Tag>}
                {company.deletion !== null && <Tag color="bad">{copy.deletionRequested}</Tag>}
                {company.status === 'active' && (
                  <Button variant="default" size="xs" onClick={() => setForm({ open: true, value: company.name, error: null })}>{copy.rename}</Button>
                )}
                {company.status !== 'active' && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px 10px' }}>
                    <Button variant="default" size="xs" disabled>{copy.rename}</Button>
                    <span className="grv-muted" style={{ fontSize: 'var(--mantine-font-size-sm)' }}>{copy.renameLocked}</span>
                  </div>
                )}
              </div>
            )}
            <span className="grv-muted">
              {`${copy.createdOn(formatDay(company.createdAt, copy))} · ${origin(company, copy)}`}
            </span>
          </>
        )}
      </div>

      {view.status === 'loading' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }} aria-busy="true">
          <Bar width="220px" /><Bar width="320px" /><Bar width="260px" />
        </div>
      )}

      {view.status === 'failed' && (
        <div style={{ ...FRAME, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px 16px', padding: 24 }}>
          <span className="grv-muted" role="alert">{copy.companyFailed}</span>
          <Button variant="default" onClick={onRetry}>{copy.retry}</Button>
        </div>
      )}

      {view.status === 'gone' && (
        <div style={{ ...FRAME, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px 16px', padding: 24 }}>
          <span className="grv-muted" role="alert">{copy.companyGone}</span>
          <Button variant="default" disabled={saving} onClick={() => onBack(true)}>{copy.back}</Button>
        </div>
      )}

      {company && company.deletion !== null && (
        <FrozenPanel
          key={company.id}
          company={company}
          deletion={company.deletion}
          copy={copy}
          onRestored={onRestored}
          onReload={onRestoredElsewhere}
          onGone={() => onBack(true)}
          onLeave={onLeave}
          onPurging={(on) => {
            setPurging(on)
            onPending(on)
          }}
        />
      )}

      {company && (
        <>
          <Section title={copy.owners}>
            <Lines items={company.owners} copy={copy} />
          </Section>

          <Section title={copy.members}>
            <div style={FRAME}>
              <Table>
                {!narrow && (
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th><span className="grv-label">{copy.email}</span></Table.Th>
                      <Table.Th><span className="grv-label">{copy.role}</span></Table.Th>
                      <Table.Th><span className="grv-label">{copy.joined}</span></Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                )}
                <Table.Tbody>
                  {company.members.map((m) => (
                    <Table.Tr key={m.email}>
                      <Table.Td style={TOP}><span style={{ overflowWrap: 'anywhere' }}>{m.email}</span></Table.Td>
                      <Table.Td style={TOP}>{copy.roles[m.role]}</Table.Td>
                      <Table.Td style={TOP}><span className="grv-num">{formatDayShort(m.joinedAt, copy)}</span></Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </div>
            <Muted>{copy.membersNote}</Muted>
          </Section>

          <Section title={copy.claimedPortfolios}>
            {company.isDemo ? (
              <Muted>{copy.demoNoPortfolio}</Muted>
            ) : (
              <>
                {company.claims.length === 0 && <Muted>{copy.none}</Muted>}
                {company.claims.map((claim) => (
                  <div key={claim.businessId} data-testid="claim" style={{ ...FRAME, display: 'flex', flexDirection: 'column', gap: 6, padding: '12px 16px' }}>
                    <span style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 8px', alignItems: 'baseline' }}>
                      <span style={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{claim.name}</span>
                      {claim.name !== claim.businessId && <span className="grv-muted grv-num">{claim.businessId}</span>}
                    </span>
                    {claim.accounts.map((account) => (
                      <span key={account.platformAccountId} data-testid="claim-account" style={{ overflowWrap: 'anywhere' }}>
                        <span className="grv-num">{account.platformAccountId}</span>
                        {` · ${account.name} · ${account.connected ? copy.connected : copy.notConnected}`}
                        {account.withdrawn && <span className="grv-muted">{` · ${copy.noLongerShared}`}</span>}
                      </span>
                    ))}
                  </div>
                ))}
                <Muted>{copy.managedOnPortfolios}</Muted>
              </>
            )}
          </Section>
        </>
      )}
    </>
  )
}
